import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doctorService } from '@/services/api';
import { formatPhone } from '@/lib/utils';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Phone,
  Mail,
  Building2,
  ShieldCheck,
  Award,
  Stethoscope,
  CheckCircle,
  User,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { cn, formatDoctorName, getDoctorInitials } from '@/lib/utils';
import { useClinic } from '@/contexts/ClinicContext';

const dayLabels = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DoctorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { buildUrl } = useClinic();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDoctor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDoctor = async () => {
    try {
      setLoading(true);
      const data = await doctorService.getById(id);
      setDoctor(data);
    } catch (error) {
      toast.error('Failed to load doctor profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-5xl">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="p-12 text-center">
        <h3 className="text-base font-bold text-slate-800">Doctor record not found</h3>
        <Button onClick={() => navigate(buildUrl('doctors'))} className="mt-4">
          Back to Doctors Directory
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="doctor-detail-page">
      {/* Top Breadcrumb & Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(buildUrl('doctors'))}
          className="text-slate-600 hover:text-slate-900 w-fit"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Doctors Directory
        </Button>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => navigate(buildUrl(`appointments/new?doctorId=${doctor.id}`))}
            className="bg-teal-600 hover:bg-teal-700 font-bold text-white text-xs shadow-sm"
            data-testid="book-slot-btn"
          >
            <Calendar className="h-4 w-4 mr-1.5" /> Book Patient Slot
          </Button>
        </div>
      </div>

      {/* Hero Profile Banner */}
      <Card className="border-slate-200 shadow-sm overflow-hidden bg-gradient-to-r from-slate-900 to-teal-950 text-white">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <Avatar className="h-20 w-20 ring-4 ring-teal-400/30 bg-teal-800 text-teal-100 font-bold text-2xl">
                <AvatarImage src={doctor.avatar} />
                <AvatarFallback>{getDoctorInitials(doctor)}</AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h1 className="text-2xl font-bold tracking-tight">
                  {formatDoctorName(doctor)}
                </h1>
                <p className="text-sm font-semibold text-teal-300">
                  {doctor.specialization} • {doctor.qualification}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-300 pt-1">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-teal-400" />
                    Reg No: {doctor.registrationNo}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Award className="h-3.5 w-3.5 text-teal-400" />
                    {doctor.experience} yrs Experience
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-teal-400" />
                    {doctor.clinics?.map(c => c.name).join(', ') || 'General OPD'}
                  </span>
                </div>
              </div>
            </div>

            <Button
              onClick={() => navigate(buildUrl(`appointments/new?doctorId=${doctor.id}`))}
              className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold shadow-md"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Book Appointment
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs Layout: Weekly Schedule, Appointments, Profile Details */}
      <Tabs defaultValue="schedule" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 border border-slate-200">
          <TabsTrigger value="schedule" className="text-xs data-[state=active]:bg-white font-medium">
            <Clock className="h-3.5 w-3.5 mr-1.5" /> Weekly OPD Schedule
          </TabsTrigger>
          <TabsTrigger value="appointments" className="text-xs data-[state=active]:bg-white font-medium">
            <Calendar className="h-3.5 w-3.5 mr-1.5" /> Assigned Appointments ({doctor.appointments?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="about" className="text-xs data-[state=active]:bg-white font-medium">
            <User className="h-3.5 w-3.5 mr-1.5" /> Professional Overview
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: WEEKLY OPD SCHEDULE */}
        <TabsContent value="schedule" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900">
                Weekly Consultation Timings & Slot Availability
              </CardTitle>
              <CardDescription className="text-xs">
                Standard OPD hours configured for this physician
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {dayLabels.map((day) => {
                  const daySched = doctor.schedule?.[day];
                  return (
                    <div
                      key={day}
                      className={cn(
                        'p-4 rounded-xl border transition-all text-xs space-y-1.5',
                        daySched ? 'bg-slate-50/70 border-slate-200' : 'bg-slate-50/20 border-slate-100 opacity-60'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold uppercase tracking-wider text-slate-900 text-xs">
                          {day}
                        </span>
                        <Badge
                          variant="outline"
                          className={daySched ? 'bg-teal-50 text-teal-800 border-teal-200 text-[10px]' : 'bg-slate-100 text-slate-500 text-[10px]'}
                        >
                          {daySched ? 'Available' : 'Off Duty'}
                        </Badge>
                      </div>
                      {daySched ? (
                        <>
                          <p className="text-slate-700 font-semibold pt-1">
                            {daySched.start} — {daySched.end}
                          </p>
                          <p className="text-slate-400 text-[11px]">
                            {daySched.slotDuration || 30} mins slot duration
                          </p>
                        </>
                      ) : (
                        <p className="text-slate-400 italic pt-1">No scheduled OPD sessions</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: APPOINTMENTS */}
        <TabsContent value="appointments" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900">
                Scheduled Appointments Queue
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {doctor.appointments?.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-400">
                  No upcoming appointments scheduled for this doctor.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-xs">
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Clinic</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {doctor.appointments?.map((apt) => (
                      <TableRow key={apt.id} className="text-xs hover:bg-slate-50/50">
                        <TableCell className="font-semibold text-slate-900">
                          {apt.date} • {apt.time}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-slate-800">{apt.patient?.firstName} {apt.patient?.lastName}</span>
                        </TableCell>
                        <TableCell>{apt.clinic?.name || 'General OPD'}</TableCell>
                        <TableCell className="capitalize">{apt.type}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] uppercase font-bold">
                            {apt.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(buildUrl(`appointments/${apt.id}`))}
                            className="h-7 text-xs"
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: PROFESSIONAL OVERVIEW */}
        <TabsContent value="about" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900">
                Professional Bio & Contact Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-xs text-slate-700">
              <p className="leading-relaxed bg-slate-50 p-4 rounded-lg border border-slate-100">
                {doctor.about || 'Senior specialist providing high quality outpatient and clinical consultations.'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-3 rounded-lg border border-slate-100 flex items-center gap-3">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-[11px] text-slate-400 font-semibold uppercase">Official Contact</p>
                    <p className="font-bold text-slate-800 font-mono">{formatPhone(doctor.phone)}</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg border border-slate-100 flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-[11px] text-slate-400 font-semibold uppercase">Hospital Email</p>
                    <p className="font-bold text-slate-800">{doctor.email}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DoctorDetailPage;

